import React, { Component } from "react";
import _ from "lodash";

// component library
import Button, { BUTTON_SIZE, BUTTON_DISPLAY } from "@acadia-ui/button";
import { TOOLBAR_EVENT_TYPE, TOOLBAR_MODE, EDIT_BUTTON_LOCATION } from "@acadia-ui/toolbar";
import Loading from "@acadia-ui/loading";

// flux stores
import PlacementDataStore from "../../../../Flux/Stores/PlacementDataStore";
import PackagesDataStore from "../../../../Flux/Stores/PackagesDataStore";
import WorkbookStore from "../../../../Flux/Stores/WorkbookStore";
import BookFiltersStore from "../../../../Flux/Stores/BookFiltersStore";
import MasterCampaignsStore from "../../../../Flux/Stores/MasterCampaignsStore";
import TraffickingDataStore from "../../../../Flux/Stores/TraffickingDataStore";

// flux actions
import { fetchEnabledColumns, insertUpdateEnabledColumns } from "../../../../Flux/Actions/WorkbookActions";

// common components
import Table from "../../../../Components/Table/Table";
import Textbox, { TEXTBOX_SIZE } from "@acadia-ui/textbox";

// local components
import FullFiltersSidesheet, { fullFilterAlgorithm } from "../FullFiltersSidesheet";
import { showToast, ToastNotification, TOAST_TYPE } from "@acadia-ui/toast";
import Modal from "@acadia-ui/modal";

// stylesheets
import "../../../Modals/ErrorModal.scss";

class LegacyPlacementView extends Component {
	constructor() {
		super();
		this.state = {
			syncStatus: null,
			loading: false,
			displayErrorBanner: false,
			displayErrorModal: false,
			errorMessages: []
		};
	}

	componentDidMount = () => {
		PlacementDataStore.on("placement_success", this.updateSheetAndPackageTab);
		PlacementDataStore.on("placement_delete_success", this.updateSheetAndPackageTabOnDelete);
		PlacementDataStore.on("placements_refetched", this.refreshSheet);
		PlacementDataStore.on("packages_loaded_refresh", this.refreshSheet);
		PlacementDataStore.on("placement_delete_fail", this.displayDeletePlacementErrors);
		PlacementDataStore.on("placements_loaded", this.getPlacements);
		PackagesDataStore.on("delete_success_placement", this.packageWithPlacementsDeleted);
		PlacementDataStore.on("sync_success", this.placementSyncStatusUpdate);
		PlacementDataStore.on("sync_fail", this.placementSyncStatusUpdate);
		PlacementDataStore.on("sync_status_updated", this.sheetChanged);
		PlacementDataStore.on("placements_lock_unlock_success", this.refreshSheet);
		PlacementDataStore.on("placements_pushed_to_GCM", this.refetchPlacements);
		TraffickingDataStore.on("mapping_fail", this.displayMappingError);
	};

	componentWillUnmount() {
		PlacementDataStore.removeListener("placement_success", this.updateSheetAndPackageTab);
		PlacementDataStore.removeListener("placement_delete_success", this.updateSheetAndPackageTabOnDelete);
		PlacementDataStore.removeListener("placements_refetched", this.refreshSheet);
		PlacementDataStore.removeListener("packages_loaded_refresh", this.refreshSheet);
		PlacementDataStore.removeListener("placement_delete_fail", this.displayDeletePlacementErrors);
		PlacementDataStore.removeListener("placements_loaded", this.getPlacements);
		PackagesDataStore.removeListener("delete_success_placement", this.packageWithPlacementsDeleted);
		PlacementDataStore.removeListener("sync_success", this.placementSyncStatusUpdate);
		PlacementDataStore.removeListener("sync_fail", this.placementSyncStatusUpdate);
		PlacementDataStore.removeListener("sync_status_updated", this.sheetChanged);
		PlacementDataStore.removeListener("placements_lock_unlock_success", this.refreshSheet);
		PlacementDataStore.removeListener("placements_pushed_to_GCM", this.refetchPlacements);
		TraffickingDataStore.removeListener("mapping_fail", this.displayMappingError);
	}

	displayMappingError = () => {
		this.setState({ displayErrorBanner: true, errorMessages: TraffickingDataStore.getMappingErrors() });
	};

	packageWithPlacementsDeleted = () => {
		this.sheetChanged();
		if (PlacementDataStore.getDeletedPackageID() === PlacementDataStore.getFilterPackageID()) {
			this.props.refresh();
			PlacementDataStore.resetSelectedPackage();
			PlacementDataStore.resetSelectedPackageID();
			PlacementDataStore.resetFilteredPlacementData();
			PlacementDataStore.resetDeletedPackageID();
		}
	};

	updateSheetAndPackageTabOnDelete = async () => {
		this.setState({ loading: true });
		await this.props.updatePackages();
		await this.props.refresh();
		await this.sheetChanged();
		this.setState({ loading: false });
		showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} message="Placement deleted successfully." timed={5000} />);
	};

	updateSheetAndPackageTab = async () => {
		this.setState({ loading: true });
		await this.props.updatePackages();
		await this.props.refresh();
		await this.sheetChanged();
		this.setState({ loading: false });
	};

	displayDeletePlacementErrors = () => {
		const errors = PlacementDataStore.getDeletePlacementErrors();
		if (Array.isArray(errors)) {
			errors.forEach((error) => {
				setTimeout(() => showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={error} />), 0);
			});
		}
		this.props.updateCounts();
		// this.sheetChanged();
	};

	refreshSheet = () => {
		this.sheetChanged();
		if (this.props.searchProps.searchValue !== "") {
			const searchableFields = [];
			const searchedFilteredData = [];
			const configKeys = Object.keys(this.props.config);
			configKeys.forEach((bucket) => {
				this.props.config[bucket].forEach((field) => {
					if (field.IsSummarizedHeader === true) {
						searchableFields.push(field.Name);
					}
				});
			});
			this.props.originalData.forEach((placement) => {
				for (const field of searchableFields) {
					if (placement[field]) {
						if (placement[field].toLowerCase().includes(this.props.searchProps.searchValue.toLowerCase())) {
							searchedFilteredData.push(placement);
							break;
						}
					}
				}
			});
			this.props.setRenderedData(searchedFilteredData);
		}
	};

	getPlacements = () => {
		const placementData = PlacementDataStore.getData();
		let siteOptions = null;
		this.props.config.PlacementData.forEach((option) => {
			if (option.Name === "PlatformSiteID") {
				siteOptions = option.Options;
			}
		});
		if (siteOptions !== null) {
			placementData.forEach((placement) => {
				if (placement.PlatformSiteID !== null) {
					let platformName = null;
					siteOptions.forEach((site) => {
						if (placement.PlatformSiteID === site.OptionID) {
							platformName = site.OptionLabel;
						}
					});
					if (platformName !== null) {
						placement.PlatformSiteID = platformName;
					}
				}
			});
		}

		let preparredData;

		const filterKeys = Object.keys(this.props.dataTableApi?.current?.toolbarApi?.current?.filterValues ?? {});
		if (filterKeys?.length > 0) {
			this?.props?.dataTableApi?.current?.dataProvider?.update?.(placementData);
			preparredData = fullFilterAlgorithm({
				handleFilter: () => {},
				config: this.props.config,
				data: this.props.originalData,
				dataTableApi: this.props.dataTableApi,
				filterOptions: Object.values(this.props.dataTableApi?.current?.toolbarApi?.current?.filterValues ?? {}),
				hierarchyLevel: "Placement"
			});
		} else {
			preparredData = PlacementDataStore.getData();
		}

		let newData = preparredData;
		if (PlacementDataStore.getFilterPackage() !== null && PlacementDataStore.getFilterPackageID() !== null) {
			const filteredData = newData.filter((placement) => {
				return placement.PackageID === PlacementDataStore.getFilterPackageID();
			});
			newData = filteredData.length ? filteredData : PlacementDataStore.getFilteredData();
		}
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		const placementTX = urlParams.get("Placement");
		if (placementTX) {
			this.handleCrossTrafficQANavigation(placementTX);
		} else {
			this.props.setRenderedData(newData);
		}
	};

	handleCrossTrafficQANavigation = (taxonomyString) => {
		const filteredData = this.returnSearchedValues(taxonomyString);
		if (filteredData.length !== 0) {
			setTimeout(() => {
				this.props.setRenderedData(filteredData);
				this.props.searchProps.onSearch(taxonomyString);
			}, 1);
		}

		setTimeout(() => {
			this.props.dataTableApi?.current?.toolbarApi?.current?.setSearchValue(taxonomyString);
		}, 500);
		setTimeout(() => {
			window.history.replaceState({}, document.title, "/Workbook");
		}, 1000);
	};

	refetchPlacements = async (fetchMetaData = false) => {
		// const choices = JSON.parse(JSON.stringify(BookFiltersStore.getChoicesState()));
		// const auditLevelID = this.props.auditLevelData.find(({ AuditLevelName }) => AuditLevelName === "Placement")?.AuditLevelID;

		// const getPlacements = fetchOnlyPlacements(
		// 	// this comment exists to prevent eslint from making this unreadable.
		// 	choices,
		// 	MasterCampaignsStore.getParentCampaignUniqueID(),
		// 	this.props.config,
		// 	undefined,
		// 	auditLevelID
		// );

		const update = async () => {
			// await this.props.updateCounts("Placement");
			await this.props.fetch();
			this.getPlacements();
			if (fetchMetaData) {
				this.props.updateMetaData();
			}
			setTimeout(() => {
				// this.props.flip();
			}, 1);
			this.props.updatePackages();
			this.props.refresh();
		};

		await update();

		// getPlacements.then(update);
	};

	sheetChanged = async () => {
		const placementData = PlacementDataStore.getData();
		let siteOptions = null;
		this.props.config.PlacementData.forEach((option) => {
			if (option.Name === "PlatformSiteID") {
				siteOptions = option.Options;
			}
		});
		if (siteOptions !== null) {
			placementData.forEach((placement) => {
				if (placement.PlatformSiteID !== null) {
					let platformName = null;
					siteOptions.forEach((site) => {
						if (placement.PlatformSiteID === site.OptionID) {
							platformName = site.OptionLabel;
						}
					});
					if (platformName !== null) {
						placement.PlatformSiteID = platformName;
					}
				}
			});
		}
		this.props.setRenderedData(placementData);
		await this.props.updateMetaData();
		this.props.flip();
	};

	returnSearchedValues = (searchTerm) => {
		const searchableFields = [];
		const filteredData = [];
		const configKeys = Object.keys(this.props.config);
		configKeys.forEach((bucket) => {
			if (Array.isArray(this.props.config[bucket])) {
				this.props.config[bucket].forEach((field) => {
					if (field.IsSummarizedHeader === true || field.Name === "PlacementName") {
						searchableFields.push(field.Name);
					}
				});
			}
		});
		this.props.originalData.forEach((data) => {
			let matchFound = false;
			for (const field of searchableFields) {
				if (data[field]) {
					if (data[field].toLowerCase().includes(searchTerm.toLowerCase())) {
						filteredData.push(data);
						matchFound = true;
						break;
					}
				}
			}
			if (matchFound === false && data.PlatformPlacementPK) {
				const targetValue = JSON.parse(data.PlatformPlacementPK)?.find(({ MasterPlatformID }) => MasterPlatformID == this.props?.campaignMetaData?.PlatformID);
				if (targetValue?.PlatformPK && targetValue?.PlatformPK.toLowerCase().includes(searchTerm.toLowerCase())) {
					filteredData.push(data);
				}
			}
		});
		return filteredData;
	};

	placementSyncStatusUpdate = () => {
		this.setState(
			{
				syncStatus: typeof PlacementDataStore.getSyncStatus() === "string" ? PlacementDataStore.getSyncStatus() : "Error Syncing Placements"
			},
			() => {
				this.refetchPlacements();
			}
		);
	};

	handleToolbarEvent = (type, { value, action, e }) => {
		switch (type) {
			case TOOLBAR_EVENT_TYPE.SETTINGS_SIDESHEET_SAVED:
				this.saveShowHideColumns(value);
				break;
			case TOOLBAR_EVENT_TYPE.SEARCH_CHANGE:
				this.props.searchProps.onSearch(value);
				break;
			default:
			// noop
		}
	};

	saveShowHideColumns = async (value) => {
		const { columnVisibility } = value;
		const columnVisKeyMap = {
			PlatformStatus: "ExternalSystemStatusID",
			GooglePlacementID: "GoogleCampaignManagerPK",
			DoNotUse: "IsDoNotUse",
			PrismaID: "PlatformPlacementPK",
			Status: "TraffickingPlacementStatusID",
			GroupNumber: "TraffickingRequestGroupNumbers"
		};

		const columns = this.props.extraColumns?.map((column) => ({ ...column, selected: columnVisibility[columnVisKeyMap[column.id]] }));
		const { auditLevelData, campaignMetaData } = this.props;
		const auditLevel = auditLevelData.find(({ AuditLevelName }) => AuditLevelName == "Placement")?.AuditLevelID;
		const { ApplicationUserSettingsID } = WorkbookStore.getEnabledColumns()?.find(({ HierarchyLevelID }) => HierarchyLevelID == auditLevel) || {};

		await insertUpdateEnabledColumns({
			ApplicationUserSettingsID,
			CampaignID: campaignMetaData.CampaignUniqueIDCode,
			HierarchyLevelID: auditLevel,
			ColumnHeaders: columns.reduce((acc, { id, selected }) => {
				acc[id] = selected;
				return acc;
			}, {})
		});

		await fetchEnabledColumns(WorkbookStore.getMetaData().CampaignUniqueIDCode);
	};

	handleFilter = (filteredData, applyFilter) => {
		if (applyFilter === false) {
			this.props.setSelectedPackage(null);
		}
	};

	render() {
		const {
			// this comment exists to prevent eslint from making this unreadable.
			refetchPlacements,
			handleToolbarEvent,
			handleFilter
		} = this;

		const {
			// this comment exists to prevent eslint from making this unreadable.
			filterOptions,
			extraColumns,
			gcmStatus,
			config,
			TMEnabled,
			campaignTMEnabled,
			clientTMEnabled,
			campaignMetaData,
			auditLevelData,
			placementAPI,
			dataTableApi,
			actionBarProps,
			originalData,
			renderedData,
			setRenderedData,
			setSelectedRows,
			searchProps,
			displayPrismaStatusFilter,
			rowHeight,
			setRowHeight
		} = this.props;

		const { searchElement, searchValue } = searchProps;

		const { displayErrorBanner } = this.state;

		const isPrisma = campaignMetaData.PlatformName == "Mediaocean (OX)/Prisma";
		const showSettingsSideSheet = isPrisma && (extraColumns?.length > 0 || config?.ParentData?.find(({ Name }) => Name == "IsDoNotUse"));

		const filtersSideSheet = (
			<FullFiltersSidesheet
				// this comment exists to prevent eslint from making this unreadable.
				displayPrismaStatusFilter={displayPrismaStatusFilter && isPrisma}
				TMEnabled={TMEnabled}
				hierarchyLevel="Placement"
				ref={dataTableApi}
				data={originalData}
				config={config}
				handleFilter={handleFilter}
			/>
		);

		return (
			<section className="tab__container">
				{displayErrorBanner && (
					<>
						<ToastNotification
							className="mapping-errors-banner"
							message="Error occured"
							type={TOAST_TYPE.ERROR}
							actionTitle="Show details"
							action={() => this.setState({ displayErrorModal: true })}
							onClose={() => this.setState({ displayErrorBanner: false })}
						/>
						<Modal
							className="error-modal"
							heading="Error"
							icon="icon-alert"
							isOpen={this.state.displayErrorModal}
							onClick={() => {}}
							onRequestClose={() => this.setState({ displayErrorModal: false })}
							actionSetProps={{
								actionsRight: (
									<Button onClick={() => this.setState({ displayErrorModal: false })} data-tracking-id-alt="close-placement-error-modal">
										Done
									</Button>
								)
							}}
						>
							<div className="additional-errors-container">
								{this.state.errorMessages.map((error) => {
									return <div className="additional-error">{error}</div>;
								})}
							</div>
						</Modal>
					</>
				)}
				{this.state.loading ? (
					<Loading fullscreen />
				) : (
					<Table
						ref={dataTableApi}
						data={renderedData}
						config={config}
						hierarchyLevel="Placement"
						metaData={campaignMetaData}
						updateSelected={setSelectedRows}
						refetchData={refetchPlacements}
						search={searchValue}
						placementAPI={placementAPI} // displayPrismaStatusFilter
						JIRAStatus={isPrisma ? campaignTMEnabled : clientTMEnabled}
						extraColumns={extraColumns}
						gcmStatus={gcmStatus}
						rowHeight={rowHeight}
						setRowHeight={setRowHeight}
						auditLevelID={auditLevelData.find(({ AuditLevelName }) => AuditLevelName == "Placement")?.AuditLevelID}
						actionBarProps={{
							search: (
								<Textbox
									debounceTimeout={500}
									size={TEXTBOX_SIZE.SMALL}
									onClear={(e) => {
										setTimeout(() => {
											dataTableApi?.current?.dataProvider?.search("");
											this.props.dataTableApi?.current?.toolbarApi?.current?.setSearchValue("");
											searchProps.onSearch("");
										}, 100);
										// setRenderedData(originalData);
									}}
								>
									Search
								</Textbox>
							),
							onToolbarEvent: handleToolbarEvent,
							settingsSideSheet: showSettingsSideSheet,
							filtersSideSheet,
							editButton: (
								<Button display={BUTTON_DISPLAY.SECONDARY} size={BUTTON_SIZE.SMALL} style={{ order: 2 }} data-tracking-id-alt="open-edit-placement-details-modal">
									Edit Details
								</Button>
							),
							defaultFilterValues: this.props.defaultFilterValues,
							showEditMode: EDIT_BUTTON_LOCATION.DEFAULT_MODE_ACTIONS,
							...actionBarProps
						}}
						doFilter={(rows, filters) => {
							const filteredData = fullFilterAlgorithm({
								handleFilter: () => {},
								config: this.props.config,
								data: this.props.dataTableApi.current?.dataProvider?.data,
								dataTableApi: this.props.dataTableApi,
								filterOptions: Object.values(filters || {}) || [],
								hierarchyLevel: "Placement"
							});

							return filteredData;
						}}
					/>
				)}
			</section>
		);
	}
}

export default LegacyPlacementView;
